// @ts-nocheck
import { isEmpty } from 'lodash-es'

import { CONSTANTS } from '../constants'
import Account from '../../mobx/Account'
import Application from '../../mobx/Application'
import * as smartLook from './sdk/smartLook'

import * as gtm from './sdk/googleTagManager'
import * as nativeApp from './sdk/nativeApp'
import { analyticsAdapter } from './sdk/AnalyticsAdapter'
import type { SetSuperPropertiesAction } from './AnalyticsManager/AnalyticsManager.types'
import { ActionDataKey } from './AnalyticsManager/AnalyticsManager.types'
import { getECommerceDomainByEnv, getPlatformId } from 'utils/utils'

const consumers = {}

export const bannerViewedMap = {}

export async function initAnalytics(params, isMobileApp) {
	// TODO - can I loop over the params.analytics and dynamically await the required Promise by a concatenated key name?
	// if not, then I need to manyally add each analytics SDK here
	const _analytics = params.analytics

	if (_analytics?.smartLook?.token && _analytics.smartLook?.region) {
		smartLook.init(_analytics.smartLook.token, _analytics.smartLook.region)
	}

	if (_analytics?.googleTagManager?.id || _analytics?.googleTagManager?.ids?.length) {
		await gtm.init(params)
		consumers.gtm = gtm
	} else {
		// When customer do not work with Google anlytics (maybe work with different tool).
		console.info(`Missing Google Tag Mgr Id!`)
	}

	if (isMobileApp) {
		consumers.nativeApp = nativeApp
	}

	// integrate analytics manager
	consumers.analyticsAdapter = analyticsAdapter
	await consumers.analyticsAdapter.init(params.c, getECommerceDomainByEnv())
}

/**
 * Any non-enhanced-ecommerce events.
 *
 * Send to all analytics SDKs that are in the params json that have an id
 *
 * Each SDK will map our json to its required json format
 *
 * @param json
 */
export function sendCustomEvent(json) {
	Object.keys(consumers).forEach((_key, _idx) => {
		const _consumer = consumers[_key]

		const _accountUser = Account.getUser()
		if (_accountUser && _accountUser.user) {
			// add user contact info if its available
			const _userContactInfo = _accountUser.user

			json.uuid = _userContactInfo.userConsistentId || ''
			json.first_name = _userContactInfo.firstName || ''
			json.last_name = _userContactInfo.lastName || ''
			json.email = _userContactInfo.email || ''
			/**
			 * This is an ugly solution to keep sending the most updated phone number (with the country code)
			 * In the future we must simplify the Account Mobx store. Move It to proper typescript and define all the needed types
			 * We also need to understand why do we maintain so many sources for the phone number (not mentioning other duplications...):
			 * Account.user.phoneNumber, 
			 * Account.user.phone, 
			 * Account.personalInfo.phone
			 */
			json.phone_number = emptyStringInsteadZero(_userContactInfo?.phoneNumber) || emptyStringInsteadZero(Account?.personalInfo?.phone)|| emptyStringInsteadZero(_userContactInfo?.phone) || ''
		}
		try {
			json.session_channel = Application.isMobileApp ? CONSTANTS.CHANNEL_TYPE.APP.toLowerCase() : CONSTANTS.CHANNEL_TYPE.WEB.toLowerCase()
			_consumer.sendCustomEvent(json)
		} catch (e) {
			console.error(e)
		}
	})
}

export const identifyUser = (userId: string) => {
	consumers?.analyticsAdapter?.identifyUser(userId)
}

export const resetUser = () => {
	consumers?.analyticsAdapter?.resetUser()
}

export const setSuperProperties = (properties: SetSuperPropertiesAction) => {
	consumers?.analyticsAdapter?.setSuperProperties(properties)
}

export const setUserProperties = (properties: Record<string, unknown>) => {
	consumers?.analyticsAdapter?.setUserProperties(properties)
}

export const setGlobalProperties = (params, preferredLanguage) => {
	setSuperProperties({
		[ActionDataKey.MARKET]: params?.name || '',
		[ActionDataKey.BRAND]: params?.brand || '',
		[ActionDataKey.USER_STATUS]: Account.isAuthenticated() ? 'signed-in' : 'guest',
		[ActionDataKey.SESSION_CHANNEL]: Application.isMobileApp ? 'app' : 'web',
		[ActionDataKey.PREFERRED_LANGUAGE]: preferredLanguage,
		[ActionDataKey.CHANNEL]: getPlatformId()
	})
}

export const setUser = (userId, firstName = null, lastName = null, fullName = null, email = null, phone = null, forceIdentify = false) => {
	if(Account.isAuthenticated() || forceIdentify) {
		identifyUser(userId)
	}
	setUserProperties({
		[ActionDataKey.USER_ID]: userId,
		[ActionDataKey.USER_STATUS]: Account.isAuthenticated() ? 'signed-in' : 'guest',
		[ActionDataKey.USER_FIRST_NAME]: firstName || Account?.user?.user?.firstName || '',
		[ActionDataKey.USER_LAST_NAME]: lastName || Account?.user?.user?.lastName || '',
		[ActionDataKey.USER_FULL_NAME]: fullName || `${Account?.user?.user?.firstName || ''} ${Account?.user?.user?.lastName || ''}`,
		[ActionDataKey.USER_EMAIL]: email || Account?.user?.user?.email || '',
		[ActionDataKey.USER_PHONE_NUMBER]: phone || Account?.personalInfo.phone || '',
	})
}

const emptyStringInsteadZero = (value) => {
	if (value && value !== '0') {
		return value
	}
	return ''
}

/**
 * Enhanced ecommerce events ONLY
 *
 * Send to all analytics SDKs that are in the params json that have an id
 *
 * Each SDK will map our event to its required event format
 *
 * @param type - must be one of the defined names
 * @param json - json required for this event
 */
export function sendEnhancedEcommerceEvent(type, ...args) {
	if (CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS[type]) {
		Object.keys(consumers).forEach((_key, _idx) => {
			const _consumer = consumers[_key]
			try {
				_consumer.sendEnhancedEcommerceEvent(type, ...args)
			} catch (e) {
				console.error(e)
			}
		})
	} else {
		console.warn(`not sending event for unknown eCommerce event: ${type}`)
	}
}

export function sendPageViewEvent(path) {
	const pageView = {
		category: 'navigation',
		action: 'page view',
		label: path,
		// location.search contains query string - to add ??
		session_channel: Application.isMobileApp ? CONSTANTS.CHANNEL_TYPE.APP.toLowerCase() : CONSTANTS.CHANNEL_TYPE.WEB.toLowerCase(),
	}

	Object.keys(consumers).forEach((_key, _idx) => {
		const _consumer = consumers[_key]

		try {
			_consumer.sendPageViewEvent(pageView)
		} catch (e) {
			console.error(e)
		}
	})
}

export const flattenVariationsChoices = (_variationsChoices) => {
	const _variationsList = []
	const _flattenVariations = (variationsChoices) => {
		if (isEmpty(variationsChoices)) {
			return
		}

		variationsChoices.forEach((variationsList) => {
			if (isEmpty(variationsList)) {
				return
			}

			variationsList.forEach((variation) => {
				_variationsList.push(variation)
				_flattenVariations(variation.variationsChoices)
			})
		})
	}

	_flattenVariations(_variationsChoices)

	return _variationsList
}

export const getRestaurantAnalyticsFields = (rest) => ({
	items: rest.items,
	locale: rest.locale,
	countryCode: rest.countryCode,
	currency: rest.currency,
	sections: rest?.sections,
})
